/**
 * Hook for managing OpenAI Realtime API voice sessions
 *
 * Handles:
 * - WebSocket connection to backend proxy
 * - Microphone capture with PCM16 encoding via AudioWorklet
 * - Audio playback from AI responses
 * - State management (idle, listening, thinking, speaking)
 * - Transcript handling
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { CalendarData } from '../types/calendar';
import { getMicrophoneSupportError } from '../utils/mediaSupport';
// ── Voice transcript display config ───────────────────────────────────────────
// Assistant text is paced against received assistant audio so reading does not
// finish before Sally has spoken the same content. The small lead keeps the UI
// responsive without letting the transcript outrun the avatar.
const VOICE_TRANSCRIPT_INITIAL_DELAY_MS = 180;
const VOICE_TRANSCRIPT_TICK_MS = 80;
const VOICE_TRANSCRIPT_SPOKEN_CHARS_PER_SECOND = 11;
const VOICE_TRANSCRIPT_MIN_CHARS_PER_SECOND = 9;
const VOICE_TRANSCRIPT_MAX_CHARS_PER_SECOND = 42;
const VOICE_TRANSCRIPT_AUDIO_LEAD_MS = 140;
const VOICE_TRANSCRIPT_MAX_LEAD_CHARS = 4;
const VOICE_TRANSCRIPT_FALLBACK_CHARS_PER_TICK = 2;
const VOICE_TRANSCRIPT_MAX_CHARS_PER_TICK = 12;
const VOICE_TRANSCRIPT_FINAL_FALLBACK_DELAY_MS = 300;
const PCM24K_BYTES_PER_SECOND = 24000 * 2;
// ─────────────────────────────────────────────────────────────────────────────

type BrowserNetworkConnection = {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  type?: string;
};

// Get WebSocket URL for realtime voice
const getRealtimeWsUrl = (): string => {
  // Check for environment variable override (highest priority)
  if (process.env.REACT_APP_REALTIME_WS_URL) {
    return process.env.REACT_APP_REALTIME_WS_URL;
  }

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  const isSecure = window.location.protocol === 'https:';
  const isNgrok = host.includes('ngrok');
  const useProxy = process.env.REACT_APP_USE_PROXY === 'true';

  // For HTTPS (ngrok) or explicit proxy mode: use relative /realtime path through proxy
  // This allows single-port architecture where proxy routes /realtime to backend
  if (isSecure || isNgrok || useProxy) {
    return `${protocol}//${host}/realtime`;
  }

  // Direct local development: connect directly to realtime server.
  // Use page hostname so LAN devices (e.g. iPhone) target the dev machine, not phone localhost.
  const wsHost = window.location.hostname || 'localhost';
  return `${protocol}//${wsHost}:8766`;
};

const getClientDebugInfo = (): Record<string, unknown> => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {};
  }

  const extendedNavigator = navigator as Navigator & {
    connection?: BrowserNetworkConnection;
    mozConnection?: BrowserNetworkConnection;
    webkitConnection?: BrowserNetworkConnection;
    deviceMemory?: number;
  };
  const connection =
    extendedNavigator.connection ||
    extendedNavigator.mozConnection ||
    extendedNavigator.webkitConnection;

  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    languages: Array.from(navigator.languages || []),
    online: navigator.onLine,
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemory: extendedNavigator.deviceMemory,
    maxTouchPoints: navigator.maxTouchPoints,
    devicePixelRatio: window.devicePixelRatio,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    network: connection ? {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
      saveData: connection.saveData,
      type: connection.type,
    } : null,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    screen: {
      width: window.screen?.width,
      height: window.screen?.height,
      availWidth: window.screen?.availWidth,
      availHeight: window.screen?.availHeight,
    },
  };
};

export type RealtimeState = 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking' | 'error';

export interface GateStats {
  rms: number;
  noiseFloor: number;
  threshold: number;
  gateOpen: boolean;
  gateEnabled: boolean;
  thresholdMultiplier: number;
  minThreshold: number;
  hangoverMs: number;
  preRollMs: number;
}

export interface GateConfig {
  enabled?: boolean;
  minThreshold?: number;
  thresholdMultiplier?: number;
  hangoverMs?: number;
  preRollMs?: number;
}

export interface GateRuntimeConfig {
  enabled: boolean;
  minThreshold: number;
  thresholdMultiplier: number;
  hangoverMs: number;
  preRollMs: number;
}

export interface AudioPlaybackConfig {
  minLeadTimeSeconds: number;
  pendingEpsilonSeconds: number;
}

export type TranscriptDelay = 'minimal' | 'low' | 'medium' | 'high' | 'xhigh';
export type NoiseReductionType = 'near_field' | 'far_field' | 'off';
export type TurnDetectionType = 'semantic_vad' | 'server_vad' | 'off';
export type VadEagerness = 'low' | 'medium' | 'high' | 'auto';

export interface TranscriptTuningConfig {
  transcriptionModel: string;
  transcriptionDelay: TranscriptDelay;
  noiseReductionType: NoiseReductionType;
  turnDetectionType: TurnDetectionType;
  vadEagerness: VadEagerness;
  speechInterruptDebounceMs: number;
  minValidSpeechMs: number;
  maxShortUtteranceChars: number;
  maxPlausibleCharsPerSec: number;
}

export interface EffectiveVoiceConfig {
  backend?: Record<string, unknown>;
  turnDetection?: Record<string, unknown> | null;
  transcription?: Record<string, unknown>;
}

export const DEFAULT_GATE_CONFIG: GateRuntimeConfig = {
  enabled: true,
  minThreshold: 0.02,
  thresholdMultiplier: 3.0,
  hangoverMs: 400,
  preRollMs: 200,
};

export const DEFAULT_AUDIO_PLAYBACK_CONFIG: AudioPlaybackConfig = {
  minLeadTimeSeconds: 0.08,
  pendingEpsilonSeconds: 0.05,
};

export const DEFAULT_TRANSCRIPT_TUNING_CONFIG: TranscriptTuningConfig = {
  transcriptionModel: 'gpt-realtime-whisper',
  transcriptionDelay: 'high',
  noiseReductionType: 'far_field',
  turnDetectionType: 'server_vad',
  vadEagerness: 'low',
  speechInterruptDebounceMs: 400,
  minValidSpeechMs: 180,
  maxShortUtteranceChars: 16,
  maxPlausibleCharsPerSec: 95,
};

export interface RealtimeMessage {
  role: 'user' | 'assistant' | 'link';
  content: string;
  linkLabel?: string;
  linkUrl?: string;
}

export interface RealtimeVoiceAction {
  action: string;
  payload?: Record<string, unknown>;
  confidence?: number;
  reason?: string;
  actionId?: string;
}

export interface UseRealtimeVoiceReturn {
  // State
  state: RealtimeState;
  isConnected: boolean;
  error: string | null;
  isMicMuted: boolean;
  isInitialGreetingPlaying: boolean;

  // Transcripts (live, accumulating)
  userTranscript: string;
  assistantTranscript: string;

  // Finalized messages (in correct order: user first, then assistant)
  messages: RealtimeMessage[];

  // Actions
  startSession: (chatId?: string) => Promise<void>;
  endSession: () => void;
  interrupt: () => void;

  // Messages from ended session
  sessionMessages: RealtimeMessage[];

  // Audio level (0-1) from analyser, for amplitude-driven UI (ref-based, call to read current value)
  getAudioLevel: () => number;

  // Noise gate debug
  getGateStats: () => GateStats | null;
  configureGate: (opts: GateConfig) => void;
  getGateConfig: () => GateRuntimeConfig;
  getDefaultGateConfig: () => GateRuntimeConfig;
  configurePlayback: (opts: Partial<AudioPlaybackConfig>) => void;
  getPlaybackConfig: () => AudioPlaybackConfig;
  getDefaultPlaybackConfig: () => AudioPlaybackConfig;
  configureTranscriptTuning: (opts: Partial<TranscriptTuningConfig>) => void;
  getTranscriptTuningConfig: () => TranscriptTuningConfig;
  getDefaultTranscriptTuningConfig: () => TranscriptTuningConfig;
  getEffectiveVoiceConfig: () => EffectiveVoiceConfig | null;
  getMuteDuringSpeaking: () => boolean;
  setMuteDuringSpeaking: (val: boolean) => void;

  // Chat ID assigned by backend for this voice session
  chatId: string | null;
  showCalendar: boolean;
  calendarData?: CalendarData;
  closeCalendar: () => void;
}

export interface UseRealtimeVoiceOptions {
  onAssistantAudioChunk?: (base64Pcm24k: string) => void;
  onAssistantAudioInterrupted?: () => void;
  onVoiceStateChange?: (state: RealtimeState) => void;
  onVoiceAction?: (action: RealtimeVoiceAction) => void;
  playAssistantAudioLocally?: boolean;
  forceMicMute?: boolean;
  initialGateConfig?: Partial<GateRuntimeConfig>;
  initialPlaybackConfig?: Partial<AudioPlaybackConfig>;
  initialTranscriptTuningConfig?: Partial<TranscriptTuningConfig>;
  workspaceSlug?: string;
  agentSlug?: string;
  avatarProfileSlug?: string;
  debugMode?: boolean;
  debugProfileId?: string;
  debugProfileSnapshot?: Record<string, unknown> | null;
}

const clampNumber = (value: unknown, fallback: number, min: number, max: number): number => {
  if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) {
    return fallback;
  }
  return Math.min(max, Math.max(min, value));
};

const normalizeGateConfig = (
  nextConfig: Partial<GateRuntimeConfig>,
  baseConfig: GateRuntimeConfig,
): GateRuntimeConfig => {
  return {
    enabled: typeof nextConfig.enabled === 'boolean' ? nextConfig.enabled : baseConfig.enabled,
    minThreshold: clampNumber(nextConfig.minThreshold, baseConfig.minThreshold, 0.001, 0.2),
    thresholdMultiplier: clampNumber(
      nextConfig.thresholdMultiplier,
      baseConfig.thresholdMultiplier,
      1.0,
      10.0,
    ),
    hangoverMs: Math.round(clampNumber(nextConfig.hangoverMs, baseConfig.hangoverMs, 0, 2000)),
    preRollMs: Math.round(clampNumber(nextConfig.preRollMs, baseConfig.preRollMs, 0, 1500)),
  };
};

const normalizePlaybackConfig = (
  nextConfig: Partial<AudioPlaybackConfig>,
  baseConfig: AudioPlaybackConfig,
): AudioPlaybackConfig => {
  return {
    minLeadTimeSeconds: clampNumber(
      nextConfig.minLeadTimeSeconds,
      baseConfig.minLeadTimeSeconds,
      0.01,
      0.5,
    ),
    pendingEpsilonSeconds: clampNumber(
      nextConfig.pendingEpsilonSeconds,
      baseConfig.pendingEpsilonSeconds,
      0.005,
      0.2,
    ),
  };
};

const TRANSCRIPTION_DELAY_VALUES: TranscriptDelay[] = ['minimal', 'low', 'medium', 'high', 'xhigh'];
const NOISE_REDUCTION_VALUES: NoiseReductionType[] = ['near_field', 'far_field', 'off'];
const TURN_DETECTION_VALUES: TurnDetectionType[] = ['semantic_vad', 'server_vad', 'off'];
const VAD_EAGERNESS_VALUES: VadEagerness[] = ['low', 'medium', 'high', 'auto'];

const chooseEnumValue = <T extends string>(
  value: unknown,
  allowedValues: readonly T[],
  fallback: T,
): T => {
  if (typeof value !== 'string') {
    return fallback;
  }
  const normalized = value.trim().toLowerCase();
  return (allowedValues as readonly string[]).includes(normalized)
    ? (normalized as T)
    : fallback;
};

const normalizeTranscriptTuningConfig = (
  nextConfig: Partial<TranscriptTuningConfig>,
  baseConfig: TranscriptTuningConfig,
): TranscriptTuningConfig => {
  const modelCandidate = typeof nextConfig.transcriptionModel === 'string'
    ? nextConfig.transcriptionModel.trim()
    : '';
  return {
    transcriptionModel: modelCandidate || baseConfig.transcriptionModel,
    transcriptionDelay: chooseEnumValue(
      nextConfig.transcriptionDelay,
      TRANSCRIPTION_DELAY_VALUES,
      baseConfig.transcriptionDelay,
    ),
    noiseReductionType: chooseEnumValue(
      nextConfig.noiseReductionType,
      NOISE_REDUCTION_VALUES,
      baseConfig.noiseReductionType,
    ),
    turnDetectionType: chooseEnumValue(
      nextConfig.turnDetectionType,
      TURN_DETECTION_VALUES,
      baseConfig.turnDetectionType,
    ),
    vadEagerness: chooseEnumValue(
      nextConfig.vadEagerness,
      VAD_EAGERNESS_VALUES,
      baseConfig.vadEagerness,
    ),
    speechInterruptDebounceMs: Math.round(
      clampNumber(
        nextConfig.speechInterruptDebounceMs,
        baseConfig.speechInterruptDebounceMs,
        0,
        5000,
      ),
    ),
    minValidSpeechMs: Math.round(
      clampNumber(nextConfig.minValidSpeechMs, baseConfig.minValidSpeechMs, 50, 2000),
    ),
    maxShortUtteranceChars: Math.round(
      clampNumber(
        nextConfig.maxShortUtteranceChars,
        baseConfig.maxShortUtteranceChars,
        1,
        200,
      ),
    ),
    maxPlausibleCharsPerSec: clampNumber(
      nextConfig.maxPlausibleCharsPerSec,
      baseConfig.maxPlausibleCharsPerSec,
      10,
      300,
    ),
  };
};

const areTranscriptTuningConfigsEqual = (
  left: TranscriptTuningConfig,
  right: TranscriptTuningConfig,
): boolean => {
  return (
    left.transcriptionModel === right.transcriptionModel &&
    left.transcriptionDelay === right.transcriptionDelay &&
    left.noiseReductionType === right.noiseReductionType &&
    left.turnDetectionType === right.turnDetectionType &&
    left.vadEagerness === right.vadEagerness &&
    left.speechInterruptDebounceMs === right.speechInterruptDebounceMs &&
    left.minValidSpeechMs === right.minValidSpeechMs &&
    left.maxShortUtteranceChars === right.maxShortUtteranceChars &&
    left.maxPlausibleCharsPerSec === right.maxPlausibleCharsPerSec
  );
};

export function useRealtimeVoice(options: UseRealtimeVoiceOptions = {}): UseRealtimeVoiceReturn {
  const realtimeDebugLogging = process.env.REACT_APP_REALTIME_DEBUG_LOGGING !== 'false';
  const workspaceSlugRef = useRef(options.workspaceSlug);
  const agentSlugRef = useRef(options.agentSlug);
  const avatarProfileSlugRef = useRef(options.avatarProfileSlug);
  const debugModeRef = useRef(options.debugMode === true);
  const debugProfileIdRef = useRef(options.debugProfileId);
  const debugProfileSnapshotRef = useRef(options.debugProfileSnapshot || null);

  // State
  const [state, setState] = useState<RealtimeState>('idle');
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMicMuted, setIsMicMuted] = useState(true);
  const [userTranscript, setUserTranscript] = useState('');
  const [assistantTranscript, setAssistantTranscript] = useState('');
  const [messages, setMessages] = useState<RealtimeMessage[]>([]);
  const [sessionMessages, setSessionMessages] = useState<RealtimeMessage[]>([]);
  const [chatId, setChatId] = useState<string | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarData, setCalendarData] = useState<CalendarData | undefined>(undefined);
  const [isForceMicMute, setIsForceMicMute] = useState<boolean>(options.forceMicMute === true);
  const [isInitialGreetingPlaying, setIsInitialGreetingPlaying] = useState(false);

  const effectiveVoiceConfigRef = useRef<EffectiveVoiceConfig | null>(null);
  const audioLevelRef = useRef(0);
  const gateStatsRef = useRef<GateStats | null>(null);
  const gateConfigRef = useRef<GateRuntimeConfig>(
    normalizeGateConfig(options.initialGateConfig || {}, DEFAULT_GATE_CONFIG),
  );
  const playbackConfigRef = useRef<AudioPlaybackConfig>(
    normalizePlaybackConfig(options.initialPlaybackConfig || {}, DEFAULT_AUDIO_PLAYBACK_CONFIG),
  );
  const transcriptTuningConfigRef = useRef<TranscriptTuningConfig>(
    normalizeTranscriptTuningConfig(
      options.initialTranscriptTuningConfig || {},
      DEFAULT_TRANSCRIPT_TUNING_CONFIG,
    ),
  );

  // Refs
  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioPlayerRef = useRef<AudioPlayer | null>(null);
  const highPassFilterRef = useRef<BiquadFilterNode | null>(null);
  const compressorRef = useRef<DynamicsCompressorNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioLevelIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const muteDuringSpeakingRef = useRef<boolean>(true); // Mute mic when AI is speaking to prevent feedback
  const isMutedRef = useRef<boolean>(false); // Mute mic when AI is speaking to prevent feedback
  const isInterruptedRef = useRef<boolean>(false); // Ignore audio after interrupt until new response
  const lastSavedUserRef = useRef<string>('');
  const lastSavedAssistantRef = useRef<string>('');
  const voiceReplacementPairsRef = useRef<Array<{ word: string; voice_replacement: string }>>([]);
  const rawUserTranscriptRef = useRef<string>('');
  const hasActiveUserTranscriptDeltaRef = useRef<boolean>(false);
  const rawAssistantTranscriptRef = useRef<string>('');
  const cancelAssistantRevealRef = useRef<(() => void) | null>(null);
  const saveAssistantMessageRef = useRef<(() => void) | null>(null);
  // Char-chunk reveal state
  const revealPendingRef = useRef<string>('');   // characters waiting to be shown
  const revealDisplayedRef = useRef<string>(''); // characters already shown
  const revealTimerRef = useRef<number | null>(null);
  const revealIsFinalRef = useRef<boolean>(false);
  const revealSpeedMultiplierRef = useRef<number>(1); // 1 = normal, 10 = fast-flush on interrupt
  const revealFinalLengthRef = useRef<number>(0);
  const assistantAudioStartedAtMsRef = useRef<number | null>(null);
  const assistantAudioReceivedDurationMsRef = useRef<number>(0);
  const audioChunkCountRef = useRef(0);
  const audioBase64CharsSentRef = useRef(0);
  const lastLoggedMuteStateRef = useRef<boolean | null>(null);
  const holdMicUntilPlaybackCompleteRef = useRef<boolean>(true);
  const pendingUnmuteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const assistantAudioChunkHandlerRef = useRef<UseRealtimeVoiceOptions['onAssistantAudioChunk']>(
    options.onAssistantAudioChunk
  );
  const assistantAudioInterruptHandlerRef = useRef<UseRealtimeVoiceOptions['onAssistantAudioInterrupted']>(
    options.onAssistantAudioInterrupted
  );
  const voiceStateChangeHandlerRef = useRef<UseRealtimeVoiceOptions['onVoiceStateChange']>(
    options.onVoiceStateChange
  );
  const voiceActionHandlerRef = useRef<UseRealtimeVoiceOptions['onVoiceAction']>(
    options.onVoiceAction
  );
  const playAssistantAudioLocallyRef = useRef<boolean>(
    options.playAssistantAudioLocally !== false
  );
  const forceMicMuteRef = useRef<boolean>(options.forceMicMute === true);
  const initialGreetingPlayingRef = useRef<boolean>(false);
  const stateRef = useRef<RealtimeState>('idle');
  const isConnectedRef = useRef<boolean>(false);

  useEffect(() => {
    stateRef.current = state;
    isConnectedRef.current = isConnected;
  }, [state, isConnected]);

  useEffect(() => {
    assistantAudioChunkHandlerRef.current = options.onAssistantAudioChunk;
    assistantAudioInterruptHandlerRef.current = options.onAssistantAudioInterrupted;
    voiceStateChangeHandlerRef.current = options.onVoiceStateChange;
    voiceActionHandlerRef.current = options.onVoiceAction;
    playAssistantAudioLocallyRef.current = options.playAssistantAudioLocally !== false;
    workspaceSlugRef.current = options.workspaceSlug;
    agentSlugRef.current = options.agentSlug;
    avatarProfileSlugRef.current = options.avatarProfileSlug;
    forceMicMuteRef.current = options.forceMicMute === true;
    debugModeRef.current = options.debugMode === true;
    debugProfileIdRef.current = options.debugProfileId;
    debugProfileSnapshotRef.current = options.debugProfileSnapshot || null;
    setIsForceMicMute(forceMicMuteRef.current);
  }, [
    options.onAssistantAudioChunk,
    options.onAssistantAudioInterrupted,
    options.onVoiceStateChange,
    options.onVoiceAction,
    options.playAssistantAudioLocally,
    options.workspaceSlug,
    options.agentSlug,
    options.avatarProfileSlug,
    options.forceMicMute,
    options.debugMode,
    options.debugProfileId,
    options.debugProfileSnapshot,
  ]);

  const debugLog = useCallback((label: string, payload: Record<string, unknown>) => {
    if (!realtimeDebugLogging) return;
    try {
      console.log(`[RealtimeDebugFrontend] ${label}`, {
        ts: new Date().toISOString(),
        state,
        isMuted: isMutedRef.current,
        chatId,
        ...payload,
      });
    } catch (err) {
      console.log(`[RealtimeDebugFrontend] ${label}`, { error: String(err) });
    }
  }, [chatId, realtimeDebugLogging, state]);

  const clearPendingUnmute = useCallback(() => {
    if (pendingUnmuteTimeoutRef.current) {
      clearTimeout(pendingUnmuteTimeoutRef.current);
      pendingUnmuteTimeoutRef.current = null;
    }
  }, []);

  const applyMuteState = useCallback((newState: RealtimeState, nextMuted: boolean, reason: string) => {
    isMutedRef.current = nextMuted;
    setIsMicMuted(nextMuted);
    if (lastLoggedMuteStateRef.current !== nextMuted) {
      lastLoggedMuteStateRef.current = nextMuted;
      debugLog('mute_state_changed', {
        newState,
        isMuted: nextMuted,
        reason,
      });
    }
  }, [debugLog]);

  const setInitialGreetingPlayingState = useCallback((active: boolean) => {
    initialGreetingPlayingRef.current = active;
    setIsInitialGreetingPlaying(active);
    if (active) {
      clearPendingUnmute();
      applyMuteState(stateRef.current, true, 'initial_greeting');
    }
  }, [applyMuteState, clearPendingUnmute]);

  const scheduleMicUnmuteAfterPlayback = useCallback((newState: RealtimeState) => {
    clearPendingUnmute();

    const maybeUnmute = () => {
      const playerHasPendingAudio = audioPlayerRef.current?.hasPendingAudio() ?? false;
      if (playerHasPendingAudio) {
        pendingUnmuteTimeoutRef.current = setTimeout(maybeUnmute, 100);
        return;
      }

      pendingUnmuteTimeoutRef.current = setTimeout(() => {
        applyMuteState(newState, false, 'playback_drained');
        pendingUnmuteTimeoutRef.current = null;
      }, 300);
    };

    maybeUnmute();
  }, [applyMuteState, clearPendingUnmute]);

  const emitVoiceStateChange = useCallback((nextState: RealtimeState) => {
    const handler = voiceStateChangeHandlerRef.current;
    if (!handler) {
      return;
    }
    try {
      handler(nextState);
    } catch (err) {
      console.warn('[Realtime] onVoiceStateChange callback failed:', err);
    }
  }, []);

  const emitAssistantAudioChunk = useCallback((base64Pcm24k: string) => {
    const handler = assistantAudioChunkHandlerRef.current;
    if (!handler) {
      return;
    }
    try {
      handler(base64Pcm24k);
    } catch (err) {
      console.warn('[Realtime] onAssistantAudioChunk callback failed:', err);
    }
  }, []);

  const emitAssistantAudioInterrupted = useCallback(() => {
    const handler = assistantAudioInterruptHandlerRef.current;
    if (!handler) {
      return;
    }
    try {
      handler();
    } catch (err) {
      console.warn('[Realtime] onAssistantAudioInterrupted callback failed:', err);
    }
  }, []);

  const emitVoiceAction = useCallback((voiceAction: RealtimeVoiceAction) => {
    const handler = voiceActionHandlerRef.current;
    if (!handler) {
      return;
    }
    try {
      handler(voiceAction);
    } catch (err) {
      console.warn('[Realtime] onVoiceAction callback failed:', err);
    }
  }, []);

  const restoreVoiceTerms = useCallback((text: string): string => {
    if (!text) return text;
    const pairs = voiceReplacementPairsRef.current;
    if (!pairs.length) return text;

    let output = text;
    for (const pair of pairs) {
      if (!pair.voice_replacement || !pair.word) continue;
      const pattern = new RegExp(pair.voice_replacement.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      output = output.replace(pattern, pair.word);
    }
    return output;
  }, []);

  const normalizeTranscript = useCallback((text: string): string => {
    return (text || '').trim().replace(/\s+/g, ' ').toLowerCase();
  }, []);

  const normalizeAssistantTranscriptForDedupe = useCallback((text: string): string => {
    return normalizeTranscript(text).replace(/[.\s\u2026]+$/g, '');
  }, [normalizeTranscript]);

  const isSameAssistantTranscript = useCallback((left: string, right: string): boolean => {
    const a = normalizeAssistantTranscriptForDedupe(left);
    const b = normalizeAssistantTranscriptForDedupe(right);
    if (!a || !b) {
      return false;
    }
    if (a === b) {
      return true;
    }
    const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
    return shorter.length >= 24 && longer.startsWith(shorter);
  }, [normalizeAssistantTranscriptForDedupe]);

  const appendAssistantMessage = useCallback((content: string) => {
    const normalized = normalizeTranscript(content);
    if (!normalized) {
      return;
    }
    lastSavedAssistantRef.current = content;
    setMessages(prev => {
      const last = prev[prev.length - 1];
      if (last?.role === 'assistant' && isSameAssistantTranscript(last.content, content)) {
        return prev;
      }
      return [...prev, { role: 'assistant', content }];
    });
  }, [isSameAssistantTranscript, normalizeTranscript]);

  const flushFinalAssistantMessage = useCallback(() => {
    if (!saveAssistantMessageRef.current) {
      return;
    }
    saveAssistantMessageRef.current();
    cancelAssistantRevealRef.current?.();
    cancelAssistantRevealRef.current = null;
    revealPendingRef.current = '';
    revealIsFinalRef.current = false;
    revealSpeedMultiplierRef.current = 1;
    assistantAudioStartedAtMsRef.current = null;
    assistantAudioReceivedDurationMsRef.current = 0;
  }, []);

  /**
   * Convert ArrayBuffer to base64 string
   */
  const arrayBufferToBase64 = useCallback((buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }, []);

  /**
   * Convert base64 string to ArrayBuffer
   */
  const base64ToArrayBuffer = useCallback((base64: string): ArrayBuffer => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }, []);

  const estimatePcm24kDurationMs = useCallback((base64Pcm24k: string): number => {
    if (!base64Pcm24k) {
      return 0;
    }
    const paddingChars = base64Pcm24k.endsWith('==') ? 2 : base64Pcm24k.endsWith('=') ? 1 : 0;
    const byteLength = Math.max(0, Math.floor((base64Pcm24k.length * 3) / 4) - paddingChars);
    return (byteLength / PCM24K_BYTES_PER_SECOND) * 1000;
  }, []);

  const startAssistantRevealTimer = useCallback(() => {
    if (revealTimerRef.current !== null) {
      return;
    }

    const tick = () => {
      if (revealPendingRef.current.length > 0) {
        let charsThisTick = 0;

        if (revealSpeedMultiplierRef.current > 1) {
          charsThisTick = Math.max(
            1,
            Math.round(VOICE_TRANSCRIPT_FALLBACK_CHARS_PER_TICK * revealSpeedMultiplierRef.current),
          );
        } else {
          const audioDurationMs = assistantAudioReceivedDurationMsRef.current;
          const finalLength = revealFinalLengthRef.current;
          const estimatedCharsPerSecond =
            revealIsFinalRef.current && finalLength > 0 && audioDurationMs > 250
              ? finalLength / (audioDurationMs / 1000)
              : VOICE_TRANSCRIPT_SPOKEN_CHARS_PER_SECOND;
          const adaptiveCharsPerSecond = Math.min(
            VOICE_TRANSCRIPT_MAX_CHARS_PER_SECOND,
            Math.max(VOICE_TRANSCRIPT_MIN_CHARS_PER_SECOND, estimatedCharsPerSecond),
          );
          const maxCharsThisTick = Math.max(
            1,
            Math.min(
              VOICE_TRANSCRIPT_MAX_CHARS_PER_TICK,
              Math.ceil((adaptiveCharsPerSecond * VOICE_TRANSCRIPT_TICK_MS) / 1000),
            ),
          );
          const audioStartedAt = assistantAudioStartedAtMsRef.current;
          if (audioStartedAt !== null) {
            const elapsedSinceAudioStartMs = Math.max(0, performance.now() - audioStartedAt);
            const finalLength = revealFinalLengthRef.current;
            const canUseAudioRatio = revealIsFinalRef.current && finalLength > 0 && audioDurationMs > 250;
            const audioProgressMs = Math.min(
              elapsedSinceAudioStartMs + VOICE_TRANSCRIPT_AUDIO_LEAD_MS,
              assistantAudioReceivedDurationMsRef.current,
            );
            const allowedChars = canUseAudioRatio
              ? Math.floor((audioProgressMs / audioDurationMs) * finalLength) + VOICE_TRANSCRIPT_MAX_LEAD_CHARS
              : Math.floor((audioProgressMs / 1000) * adaptiveCharsPerSecond) + VOICE_TRANSCRIPT_MAX_LEAD_CHARS;
            const remainingBudget = allowedChars - revealDisplayedRef.current.length;
            charsThisTick = Math.max(0, Math.min(remainingBudget, maxCharsThisTick));
            if (
              charsThisTick === 0
              && revealIsFinalRef.current
              && elapsedSinceAudioStartMs >= assistantAudioReceivedDurationMsRef.current + VOICE_TRANSCRIPT_FINAL_FALLBACK_DELAY_MS
            ) {
              // Audio can finish before all final transcript text has been revealed.
              // Once the model has sent the final transcript, keep writing the tail
              // slowly instead of stalling forever on the audio-duration budget.
              charsThisTick = maxCharsThisTick;
            }
          } else if (revealIsFinalRef.current) {
            // Text-only fallback: still reveal slowly if no audio ever arrived.
            charsThisTick = maxCharsThisTick;
          }
        }

        if (charsThisTick > 0) {
          const chunk = revealPendingRef.current.slice(0, charsThisTick);
          revealPendingRef.current = revealPendingRef.current.slice(chunk.length);
          revealDisplayedRef.current += chunk;
          setAssistantTranscript(revealDisplayedRef.current);
        }
      }

      if (revealPendingRef.current.length > 0 || !revealIsFinalRef.current) {
        const delay = Math.max(30, Math.round(VOICE_TRANSCRIPT_TICK_MS / revealSpeedMultiplierRef.current));
        revealTimerRef.current = window.setTimeout(tick, delay);
      } else {
        revealTimerRef.current = null;
        saveAssistantMessageRef.current?.();
      }
    };

    revealTimerRef.current = window.setTimeout(tick, VOICE_TRANSCRIPT_INITIAL_DELAY_MS);
    cancelAssistantRevealRef.current = () => {
      if (revealTimerRef.current !== null) {
        window.clearTimeout(revealTimerRef.current);
        revealTimerRef.current = null;
      }
    };
  }, []);

  const finalizeInterruptedAssistantTranscript = useCallback(() => {
    cancelAssistantRevealRef.current?.();
    cancelAssistantRevealRef.current = null;
    saveAssistantMessageRef.current = null;

    const spokenText = revealDisplayedRef.current.trimEnd();
    if (!spokenText) {
      revealPendingRef.current = '';
      revealIsFinalRef.current = true;
      setAssistantTranscript('');
      return;
    }

    const interruptedText = `${spokenText.replace(/[.\s]+$/, '')}...`;
    rawAssistantTranscriptRef.current = interruptedText;
    revealDisplayedRef.current = interruptedText;
    revealPendingRef.current = '';
    revealIsFinalRef.current = true;
    revealFinalLengthRef.current = interruptedText.length;

    const normalized = normalizeTranscript(interruptedText);
    if (normalized && !isSameAssistantTranscript(interruptedText, lastSavedAssistantRef.current)) {
      appendAssistantMessage(interruptedText);
    }

    // The interrupted text is now a finalized message. Keeping it in the live
    // assistant transcript renders a second assistant bubble until React state
    // catches up, which is especially visible during barge-in.
    setAssistantTranscript('');
  }, [appendAssistantMessage, isSameAssistantTranscript, normalizeTranscript]);

  /**
   * Set up audio capture with AudioWorklet for PCM16 encoding
   */
  const setupAudioCapture = useCallback(async () => {
    console.log('[Realtime] Setting up audio capture...');

    // Step 1: Get microphone access
    let stream: MediaStream;
    try {
      console.log('[Realtime] Step 1: Requesting microphone access...');
      const supportError = getMicrophoneSupportError();
      if (supportError) {
        throw new Error(supportError);
      }

      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      mediaStreamRef.current = stream;
      console.log('[Realtime] Step 1: Microphone access granted');
      const [track] = stream.getAudioTracks();
      debugLog('microphone_track', {
        label: track?.label || '(unknown)',
        enabled: track?.enabled,
        muted: track?.muted,
        readyState: track?.readyState,
        settings: track?.getSettings?.(),
        constraints: track?.getConstraints?.(),
      });
      navigator.mediaDevices.enumerateDevices()
        .then((devices) => {
          debugLog('enumerate_devices', {
            audioInputs: devices
              .filter((device) => device.kind === 'audioinput')
              .map((device) => ({
                deviceId: device.deviceId,
                groupId: device.groupId,
                label: device.label,
              })),
            audioOutputs: devices
              .filter((device) => device.kind === 'audiooutput')
              .map((device) => ({
                deviceId: device.deviceId,
                groupId: device.groupId,
                label: device.label,
              })),
          });
        })
        .catch((enumerateError) => {
          debugLog('enumerate_devices_failed', {
            error: enumerateError instanceof Error ? enumerateError.message : String(enumerateError),
          });
        });
    } catch (err) {
      console.error('[Realtime] Step 1 FAILED - getUserMedia error:', err);
      throw new Error(`Microphone access denied: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }

    // Step 2: Create AudioContext with default sample rate (must match microphone)
    let audioContext: AudioContext;
    try {
      console.log('[Realtime] Step 2: Creating AudioContext with default sample rate...');
      audioContext = new AudioContext();
      console.log('[Realtime] Step 2: AudioContext created at', audioContext.sampleRate, 'Hz (will resample to 24kHz in worklet)');
      audioContextRef.current = audioContext;
    } catch (err) {
      console.error('[Realtime] Step 2 FAILED - AudioContext error:', err);
      throw new Error(`AudioContext creation failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }

    // Step 3: Load AudioWorklet
    try {
      console.log('[Realtime] Step 3: Loading AudioWorklet from /audio-processor.js...');
      await audioContext.audioWorklet.addModule('/audio-processor.js');
      console.log('[Realtime] Step 3: AudioWorklet loaded');
    } catch (err) {
      console.error('[Realtime] Step 3 FAILED - AudioWorklet load error:', err);
      throw new Error(`AudioWorklet load failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }

    // Step 4: Create worklet node with sample rate info for resampling
    let workletNode: AudioWorkletNode;
    try {
      console.log('[Realtime] Step 4: Creating AudioWorkletNode...');
      workletNode = new AudioWorkletNode(audioContext, 'pcm-processor', {
        processorOptions: {
          inputSampleRate: audioContext.sampleRate,
          outputSampleRate: 24000,
        },
      });
      workletNodeRef.current = workletNode;
      workletNode.port.postMessage({
        type: 'configure_gate',
        ...gateConfigRef.current,
      });
      console.log('[Realtime] Step 4: AudioWorkletNode created (input:', audioContext.sampleRate, '-> output: 24000 Hz)');
    } catch (err) {
      console.error('[Realtime] Step 4 FAILED - AudioWorkletNode error:', err);
      throw new Error(`AudioWorkletNode creation failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }

    // Step 5: Connect audio pipeline with filters
    try {
      console.log('[Realtime] Step 5: Connecting audio pipeline with filters...');

      // Handle messages from worklet (audio chunks + gate state)
      workletNode.port.onmessage = (event) => {
        const micTransmissionBlocked =
          isMutedRef.current || forceMicMuteRef.current || initialGreetingPlayingRef.current;
        if (
          event.data.type === 'audio' &&
          wsRef.current?.readyState === WebSocket.OPEN &&
          !micTransmissionBlocked
        ) {
          const base64Audio = arrayBufferToBase64(event.data.data);
          audioChunkCountRef.current += 1;
          audioBase64CharsSentRef.current += base64Audio.length;
          if (audioChunkCountRef.current <= 10 || audioChunkCountRef.current % 25 === 0) {
            debugLog('audio_chunk_sent', {
              chunkIndex: audioChunkCountRef.current,
              base64Chars: base64Audio.length,
              totalBase64Chars: audioBase64CharsSentRef.current,
              wsReadyState: wsRef.current?.readyState,
            });
          }
          wsRef.current.send(
            JSON.stringify({
              type: 'realtime.audio',
              audio: base64Audio,
            })
          );
        } else if (event.data.type === 'gate_state') {
          debugLog('noise_gate_state', { open: event.data.open });
        } else if (event.data.type === 'stats') {
          gateStatsRef.current = event.data as GateStats;
        }
      };

      const source = audioContext.createMediaStreamSource(stream);

      // Analyser: monitoring tap for audio levels (does not modify signal)
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      analyserRef.current = analyser;

      // VAD-isolation mode: source → worklet directly.
      // The analyser is kept as a passive monitoring tap only.
      source.connect(workletNode);
      source.connect(analyser);

      // Poll analyser for audio level (stored in ref to avoid re-renders; read via getAudioLevel)
      const dataArray = new Uint8Array(analyser.fftSize);
      audioLevelIntervalRef.current = setInterval(() => {
        analyser.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const val = (dataArray[i] - 128) / 128;
          sum += val * val;
        }
        const rms = Math.sqrt(sum / dataArray.length);
        audioLevelRef.current = Math.min(1, rms * 4);
      }, 50);

      console.log('[Realtime] Step 5: Audio pipeline connected (source → worklet, gate disabled, analyser tap only)');
      console.log('[Realtime] Audio capture ready!');
      debugLog('audio_pipeline_ready', {
        audioContextSampleRate: audioContext.sampleRate,
      });
    } catch (err) {
      console.error('[Realtime] Step 5 FAILED - Connection error:', err);
      throw new Error(`Audio connection failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [arrayBufferToBase64, debugLog]);

  /**
   * Clean up audio resources
   */
  const cleanupAudio = useCallback(() => {
      console.log('[Realtime] Cleaning up audio...');
      debugLog('cleanup_audio', {
        audioChunkCount: audioChunkCountRef.current,
        totalBase64Chars: audioBase64CharsSentRef.current,
      });
      clearPendingUnmute();

    // Stop audio level polling
    if (audioLevelIntervalRef.current) {
      clearInterval(audioLevelIntervalRef.current);
      audioLevelIntervalRef.current = null;
    }

    // Stop worklet
    if (workletNodeRef.current) {
      workletNodeRef.current.port.postMessage({ type: 'stop' });
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    // Disconnect filter nodes
    if (highPassFilterRef.current) {
      highPassFilterRef.current.disconnect();
      highPassFilterRef.current = null;
    }
    if (compressorRef.current) {
      compressorRef.current.disconnect();
      compressorRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    // Stop audio player
    if (audioPlayerRef.current) {
      audioPlayerRef.current.stop();
    }

    audioLevelRef.current = 0;
  }, [clearPendingUnmute, debugLog]);

  /**
   * Handle WebSocket message
   */
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        const type = data.type;

        switch (type) {
          case 'realtime.started':
            console.log('[Realtime] Session started:', data.session_id, 'chat_id:', data.chat_id);
            voiceReplacementPairsRef.current = Array.isArray(data.voice_replacements) ? data.voice_replacements : [];
            holdMicUntilPlaybackCompleteRef.current = data.hold_mic_until_playback_complete !== false;
            setIsInitialGreetingPlaying(false);
            if (data.transcript_tuning && typeof data.transcript_tuning === 'object') {
              const backendConfig = normalizeTranscriptTuningConfig(
                data.transcript_tuning as Partial<TranscriptTuningConfig>,
                DEFAULT_TRANSCRIPT_TUNING_CONFIG,
              );
              const desiredConfig = transcriptTuningConfigRef.current;
              if (
                !areTranscriptTuningConfigsEqual(backendConfig, desiredConfig) &&
                wsRef.current?.readyState === WebSocket.OPEN
              ) {
                wsRef.current.send(
                  JSON.stringify({
                    type: 'realtime.transcript_tuning.update',
                    config: desiredConfig,
                  }),
                );
              }
            }
            if (data.effective_voice_config && typeof data.effective_voice_config === 'object') {
              effectiveVoiceConfigRef.current = data.effective_voice_config as EffectiveVoiceConfig;
            }
            setIsConnected(true);
            setState('listening');
            emitVoiceStateChange('listening');
            if (data.chat_id) {
              setChatId(data.chat_id);
            }
            break;

          case 'realtime.initial_greeting':
            setInitialGreetingPlayingState(data.active === true);
            break;

          case 'realtime.state':
            console.log('[Realtime] State change:', data.state);
            const newState = data.state as RealtimeState;
            if (newState === 'listening' || newState === 'thinking' || newState === 'speaking') {
              setIsConnected(true);
            }
            setState(newState);
            emitVoiceStateChange(newState);
            if (newState === 'speaking') {
              clearPendingUnmute();
              applyMuteState(newState, muteDuringSpeakingRef.current, 'assistant_speaking');
              isInterruptedRef.current = false;
              // New assistant response starting — persist pending message, cancel reveal, reset
              saveAssistantMessageRef.current?.();
              cancelAssistantRevealRef.current?.();
              cancelAssistantRevealRef.current = null;
              rawAssistantTranscriptRef.current = '';
              revealPendingRef.current = '';
              revealDisplayedRef.current = '';
              revealIsFinalRef.current = false;
              revealSpeedMultiplierRef.current = 1;
              revealFinalLengthRef.current = 0;
              assistantAudioStartedAtMsRef.current = null;
              assistantAudioReceivedDurationMsRef.current = 0;
              setAssistantTranscript('');
              console.log('[Realtime] Microphone muted, assistant transcript reset for new response');
            } else if (newState === 'thinking') {
              clearPendingUnmute();
              applyMuteState(newState, muteDuringSpeakingRef.current, 'assistant_thinking');
              console.log('[Realtime] Microphone muted (thinking)');
            } else if (newState === 'listening') {
              if (muteDuringSpeakingRef.current && holdMicUntilPlaybackCompleteRef.current) {
                applyMuteState(newState, true, 'waiting_for_playback_drain');
                scheduleMicUnmuteAfterPlayback(newState);
                console.log('[Realtime] Holding microphone muted until playback drains');
              } else {
                applyMuteState(newState, false, 'listening_state');
                console.log('[Realtime] Microphone unmuted (listening)');
              }
            }
            break;

          case 'realtime.user_transcript':
            if (data.is_final) {
              rawUserTranscriptRef.current = data.transcript || '';
              hasActiveUserTranscriptDeltaRef.current = false;
              const transcript = restoreVoiceTerms(rawUserTranscriptRef.current);
              debugLog('user_transcript_final', {
                transcript,
                transcriptChars: transcript.length,
                suspicious: /daniel kohl|podcast|synaworks next it|experten-talk/i.test(transcript),
              });
              setUserTranscript(transcript);
              // Add finalized user message if not already saved
              const normalizedTranscript = normalizeTranscript(transcript);
              const normalizedLastSaved = normalizeTranscript(lastSavedUserRef.current);
              if (normalizedTranscript && normalizedTranscript !== normalizedLastSaved) {
                flushFinalAssistantMessage();
                lastSavedUserRef.current = transcript;
                setMessages(prev => [...prev, { role: 'user', content: transcript }]);
              }
            } else {
              const delta = data.transcript || '';
              if (!hasActiveUserTranscriptDeltaRef.current) {
                // First partial of a new utterance: replace stale transcript buffer.
                rawUserTranscriptRef.current = delta;
              } else {
                rawUserTranscriptRef.current += delta;
              }
              hasActiveUserTranscriptDeltaRef.current = true;
              debugLog('user_transcript_delta', {
                delta,
                transcriptSoFar: rawUserTranscriptRef.current,
              });
              setUserTranscript(restoreVoiceTerms(rawUserTranscriptRef.current));
            }
            break;

          case 'realtime.assistant_transcript':
            if (isInterruptedRef.current) {
              break;
            }
            if (data.is_final) {
              revealIsFinalRef.current = true;
              const finalTranscript = restoreVoiceTerms(data.transcript || rawAssistantTranscriptRef.current);
              revealFinalLengthRef.current = finalTranscript.length;
              revealPendingRef.current = finalTranscript.slice(revealDisplayedRef.current.length);
              startAssistantRevealTimer();
              saveAssistantMessageRef.current = () => {
                const norm = normalizeTranscript(finalTranscript);
                if (norm && !isSameAssistantTranscript(finalTranscript, lastSavedAssistantRef.current)) {
                  // Snap the live display to the final text so it exactly matches
                  // the saved message (the reveal concatenates deltas without the
                  // separator the backend uses to join multi-item responses). This
                  // lets the modal recognise the live bubble as saved and hide it.
                  revealDisplayedRef.current = finalTranscript;
                  setAssistantTranscript(finalTranscript);
                  appendAssistantMessage(finalTranscript);
                }
                saveAssistantMessageRef.current = null;
              };
            } else {
              const rawDelta = data.transcript || '';
              rawAssistantTranscriptRef.current += rawDelta;
              // Apply replacements to the full accumulated text, then re-derive
              // pending as everything after the already-displayed cursor.
              // This handles words split across deltas (e.g. "Cynerwor"+"ks")
              // and corrects partial chars already in the pending buffer.
              const fullProcessed = restoreVoiceTerms(rawAssistantTranscriptRef.current);
              revealPendingRef.current = fullProcessed.slice(revealDisplayedRef.current.length);
              startAssistantRevealTimer();
            }
            break;

          case 'realtime.audio':
            // Play AI audio (ignore if interrupted)
            if (data.audio && !isInterruptedRef.current) {
              if (assistantAudioStartedAtMsRef.current === null) {
                assistantAudioStartedAtMsRef.current = performance.now();
              }
              assistantAudioReceivedDurationMsRef.current += estimatePcm24kDurationMs(data.audio);
              startAssistantRevealTimer();

              if (audioPlayerRef.current && playAssistantAudioLocallyRef.current) {
                const pcm16Buffer = base64ToArrayBuffer(data.audio);
                audioPlayerRef.current.queueAudio(pcm16Buffer);
              }
              emitAssistantAudioChunk(data.audio);
            }
            break;

          case 'realtime.interrupt_audio':
            // Backend detected user speech - stop all audio immediately
            console.log('[Realtime] Backend interrupt - stopping audio playback');
            isInterruptedRef.current = true;
            finalizeInterruptedAssistantTranscript();
            if (audioPlayerRef.current) {
              audioPlayerRef.current.stop();
            }
            emitAssistantAudioInterrupted();
            break;

          case 'realtime.tool_call':
            console.log('[Realtime] Tool call:', data.name, data.arguments);
            break;

          case 'realtime.calendar':
            setShowCalendar(Boolean(data.showCalendar));
            setCalendarData(data.calendarData);
            break;

          case 'realtime.show_link':
            if (typeof data.label === 'string' && typeof data.url === 'string') {
              setMessages(prev => [...prev, {
                role: 'link',
                content: '',
                linkLabel: data.label,
                linkUrl: data.url,
              }]);
            }
            break;

          case 'realtime.voice_action':
            emitVoiceAction({
              action: typeof data.action === 'string' ? data.action : '',
              payload: data.payload && typeof data.payload === 'object' ? data.payload : undefined,
              confidence: typeof data.confidence === 'number' ? data.confidence : undefined,
              reason: typeof data.reason === 'string' ? data.reason : undefined,
              actionId: typeof data.action_id === 'string' ? data.action_id : undefined,
            });
            break;

          case 'realtime.transcript_tuning.updated':
            if (data.config && typeof data.config === 'object') {
              transcriptTuningConfigRef.current = normalizeTranscriptTuningConfig(
                data.config as Partial<TranscriptTuningConfig>,
                transcriptTuningConfigRef.current,
              );
            }
            if (data.effective_voice_config && typeof data.effective_voice_config === 'object') {
              effectiveVoiceConfigRef.current = data.effective_voice_config as EffectiveVoiceConfig;
            }
            break;

          case 'realtime.transcript_tuning.error':
            if (data.config && typeof data.config === 'object') {
              transcriptTuningConfigRef.current = normalizeTranscriptTuningConfig(
                data.config as Partial<TranscriptTuningConfig>,
                transcriptTuningConfigRef.current,
              );
            }
            break;

          case 'realtime.error': {
            const message = typeof data.message === 'string' ? data.message : 'Unknown realtime error';
            const isRecoverableSessionConfigWarning =
              /Unknown parameter: 'session\./i.test(message) ||
              /Missing required parameter: 'session\./i.test(message);

            console.error('[Realtime] Error:', message);
            setError(message);

            if (isRecoverableSessionConfigWarning && isConnectedRef.current) {
              console.warn('[Realtime] Recoverable realtime warning received; keeping active connection.');
              break;
            }

            setIsConnected(false);
            setIsInitialGreetingPlaying(false);
            setState('error');
            emitVoiceStateChange('error');
            break;
          }

          case 'realtime.ended':
            console.log('[Realtime] Session ended:', data.reason, 'chat_id:', data.chat_id);
            setIsConnected(false);
            setIsInitialGreetingPlaying(false);
            setState('idle');
            emitVoiceStateChange('idle');
            if (data.messages) {
              setSessionMessages(
                data.messages.map((m: RealtimeMessage) => ({
                  ...m,
                  content: restoreVoiceTerms(m.content),
                }))
              );
            }
            if (data.chat_id) {
              setChatId(data.chat_id);
            }
            break;

          case 'pong':
            // Keepalive response
            break;

          default:
            console.log('[Realtime] Unknown message:', type);
        }
      } catch (err) {
        console.error('[Realtime] Error parsing message:', err);
      }
    },
    [
      base64ToArrayBuffer,
      restoreVoiceTerms,
      normalizeTranscript,
      clearPendingUnmute,
      applyMuteState,
      scheduleMicUnmuteAfterPlayback,
      debugLog,
      emitVoiceStateChange,
      emitAssistantAudioChunk,
      emitAssistantAudioInterrupted,
      emitVoiceAction,
      estimatePcm24kDurationMs,
      startAssistantRevealTimer,
      finalizeInterruptedAssistantTranscript,
      flushFinalAssistantMessage,
      isSameAssistantTranscript,
      appendAssistantMessage,
      setInitialGreetingPlayingState,
    ]
  );

  /**
   * Start a new voice session
   */
  const startSession = useCallback(
    async (chatId?: string) => {
      try {
        setError(null);
        setUserTranscript('');
        setAssistantTranscript('');
        setMessages([]);
        setSessionMessages([]);
        setChatId(null);
        setShowCalendar(false);
        setCalendarData(undefined);
        initialGreetingPlayingRef.current = false;
        setIsInitialGreetingPlaying(false);
        lastSavedUserRef.current = '';
        lastSavedAssistantRef.current = '';
        rawUserTranscriptRef.current = '';
        hasActiveUserTranscriptDeltaRef.current = false;
        rawAssistantTranscriptRef.current = '';
        cancelAssistantRevealRef.current?.();
        cancelAssistantRevealRef.current = null;
        saveAssistantMessageRef.current = null;
        revealPendingRef.current = '';
        revealDisplayedRef.current = '';
        revealIsFinalRef.current = false;
        revealSpeedMultiplierRef.current = 1;
        revealFinalLengthRef.current = 0;
        assistantAudioStartedAtMsRef.current = null;
        assistantAudioReceivedDurationMsRef.current = 0;
        voiceReplacementPairsRef.current = [];
        isInterruptedRef.current = false;
        audioChunkCountRef.current = 0;
        audioBase64CharsSentRef.current = 0;
        lastLoggedMuteStateRef.current = null;
        holdMicUntilPlaybackCompleteRef.current = true;
        isMutedRef.current = true;
        setIsMicMuted(true);
        clearPendingUnmute();
        setState('connecting');

        console.log('[Realtime] Starting session...');
        const clientInfo = getClientDebugInfo();
        debugLog('start_session', {
          requestedChatId: chatId || null,
          debugMode: debugModeRef.current,
          clientInfo,
        });

        // Set up audio capture first
        await setupAudioCapture();

        // Initialize audio player
        audioPlayerRef.current = new AudioPlayer(playbackConfigRef.current);

        // Connect to WebSocket
        const wsUrl = getRealtimeWsUrl();
        console.log('[Realtime] Connecting to:', wsUrl);
        const wsConnectStartedAtMs = performance.now();
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('[Realtime] WebSocket connected');
          debugLog('ws_open', {
            url: wsUrl,
            connectDurationMs: Math.round(performance.now() - wsConnectStartedAtMs),
            clientInfo: getClientDebugInfo(),
          });
          // Start the realtime session
          const adminToken = sessionStorage.getItem('admin_token');
          const debugMode = debugModeRef.current;
          ws.send(
            JSON.stringify({
              type: 'realtime.start',
              chat_id: chatId,
              ...(workspaceSlugRef.current ? { workspace_slug: workspaceSlugRef.current } : {}),
              ...(agentSlugRef.current ? { agent_slug: agentSlugRef.current } : {}),
              ...(avatarProfileSlugRef.current ? { avatar_profile_slug: avatarProfileSlugRef.current } : {}),
              ...(adminToken ? { auth_token: adminToken } : {}),
              client_info: clientInfo,
              ...(debugMode ? {
                debug_mode: true,
                ...(debugProfileIdRef.current ? { debug_profile_id: debugProfileIdRef.current } : {}),
                ...(debugProfileSnapshotRef.current ? { debug_profile_snapshot: debugProfileSnapshotRef.current } : {}),
              } : {}),
            })
          );
        };

        ws.onmessage = handleMessage;

        ws.onerror = (errorEvent) => {
          const target = errorEvent.target as WebSocket | null;
          const readyState = target?.readyState;
          const stateLabel = readyState === WebSocket.CONNECTING
            ? 'CONNECTING'
            : readyState === WebSocket.OPEN
            ? 'OPEN'
            : readyState === WebSocket.CLOSING
            ? 'CLOSING'
            : readyState === WebSocket.CLOSED
            ? 'CLOSED'
            : 'UNKNOWN';
          console.error('[Realtime] WebSocket error:', {
            type: errorEvent.type,
            isTrusted: (errorEvent as Event).isTrusted,
            url: target?.url || wsUrl,
            readyState,
            readyStateLabel: stateLabel,
          });
          debugLog('ws_error', {
            url: target?.url || wsUrl,
            readyState,
            readyStateLabel: stateLabel,
            isTrusted: (errorEvent as Event).isTrusted,
            clientInfo: getClientDebugInfo(),
          });
          const activeVoiceState =
            stateRef.current === 'listening' ||
            stateRef.current === 'thinking' ||
            stateRef.current === 'speaking';
          if (readyState === WebSocket.OPEN || isConnectedRef.current || activeVoiceState) {
            console.warn('[Realtime] Ignoring transient WebSocket error during active voice session');
            return;
          }
          setError('Connection error');
          setState('error');
          emitVoiceStateChange('error');
        };

        ws.onclose = (event) => {
          console.log('[Realtime] WebSocket closed');
          debugLog('ws_close', {
            url: wsUrl,
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
            sessionDurationMs: Math.round(performance.now() - wsConnectStartedAtMs),
            audioChunkCount: audioChunkCountRef.current,
            totalBase64Chars: audioBase64CharsSentRef.current,
            clientInfo: getClientDebugInfo(),
          });
          setIsConnected(false);
          if (stateRef.current !== 'idle') {
            setState('idle');
            emitVoiceStateChange('idle');
          }
        };
      } catch (err) {
        console.error('[Realtime] Error starting session:', err);
        setError(err instanceof Error ? err.message : 'Failed to start session');
        setState('error');
        emitVoiceStateChange('error');
        cleanupAudio();
        throw err;
      }
    },
    [setupAudioCapture, handleMessage, cleanupAudio, debugLog, clearPendingUnmute, emitVoiceStateChange]
  );

  /**
   * End the current session
   */
  const endSession = useCallback(() => {
    console.log('[Realtime] Ending session...');

    // Persist any assistant message whose reveal was still in progress
    saveAssistantMessageRef.current?.();
    cancelAssistantRevealRef.current?.();
    cancelAssistantRevealRef.current = null;
    saveAssistantMessageRef.current = null;
    revealPendingRef.current = '';
    revealDisplayedRef.current = '';
    revealIsFinalRef.current = false;
    revealSpeedMultiplierRef.current = 1;
    revealFinalLengthRef.current = 0;
    assistantAudioStartedAtMsRef.current = null;
    assistantAudioReceivedDurationMsRef.current = 0;

    // Send end message
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'realtime.end' }));
    }

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Cleanup audio
    cleanupAudio();

    setIsConnected(false);
    setIsMicMuted(true);
    initialGreetingPlayingRef.current = false;
    setIsInitialGreetingPlaying(false);
    setState('idle');
  }, [cleanupAudio]);

  /**
   * Interrupt AI speech
   */
  const interrupt = useCallback(() => {
    console.log('[Realtime] Interrupting...');

    // Set flag to ignore any more audio until new response starts
    isInterruptedRef.current = true;

    finalizeInterruptedAssistantTranscript();

    // Stop audio playback and clear queue
    if (audioPlayerRef.current) {
      audioPlayerRef.current.stop();
    }

    // Send interrupt message to backend/OpenAI
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'realtime.interrupt' }));
    }
  }, [finalizeInterruptedAssistantTranscript]);

  const configureGate = useCallback((opts: GateConfig) => {
    gateConfigRef.current = normalizeGateConfig(opts, gateConfigRef.current);
    workletNodeRef.current?.port.postMessage({
      type: 'configure_gate',
      ...gateConfigRef.current,
    });
  }, []);

  const configurePlayback = useCallback((opts: Partial<AudioPlaybackConfig>) => {
    playbackConfigRef.current = normalizePlaybackConfig(opts, playbackConfigRef.current);
    audioPlayerRef.current?.setConfig(playbackConfigRef.current);
  }, []);

  const configureTranscriptTuning = useCallback((opts: Partial<TranscriptTuningConfig>) => {
    const nextConfig = normalizeTranscriptTuningConfig(opts, transcriptTuningConfigRef.current);
    transcriptTuningConfigRef.current = nextConfig;

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'realtime.transcript_tuning.update',
          config: nextConfig,
        }),
      );
    }
  }, []);

  return {
    state,
    isConnected,
    error,
    isMicMuted: isMicMuted || isForceMicMute || isInitialGreetingPlaying,
    isInitialGreetingPlaying,
    userTranscript,
    assistantTranscript,
    messages,
    startSession,
    endSession,
    interrupt,
    sessionMessages,
    getAudioLevel: () => audioLevelRef.current,
    getGateStats: () => gateStatsRef.current,
    configureGate,
    getGateConfig: () => gateConfigRef.current,
    getDefaultGateConfig: () => DEFAULT_GATE_CONFIG,
    configurePlayback,
    getPlaybackConfig: () => playbackConfigRef.current,
    getDefaultPlaybackConfig: () => DEFAULT_AUDIO_PLAYBACK_CONFIG,
    configureTranscriptTuning,
    getTranscriptTuningConfig: () => transcriptTuningConfigRef.current,
    getDefaultTranscriptTuningConfig: () => DEFAULT_TRANSCRIPT_TUNING_CONFIG,
    getEffectiveVoiceConfig: () => effectiveVoiceConfigRef.current,
    getMuteDuringSpeaking: () => muteDuringSpeakingRef.current,
    setMuteDuringSpeaking: (val: boolean) => {
      muteDuringSpeakingRef.current = val;
      if (!val && isMutedRef.current) {
        // Immediately unmute if mic is currently held muted
        applyMuteState('listening', false, 'mute_disabled_by_user');
      }
    },
    chatId,
    showCalendar,
    calendarData,
    closeCalendar: () => setShowCalendar(false),
  };
}

/**
 * Audio player for PCM16 playback
 */
class AudioPlayer {
  private audioContext: AudioContext | null = null;
  private activeSources = new Set<AudioBufferSourceNode>();
  private nextPlaybackTime = 0;
  private config: AudioPlaybackConfig;

  constructor(initialConfig: AudioPlaybackConfig) {
    this.config = { ...initialConfig };
    this.audioContext = new AudioContext({ sampleRate: 24000, latencyHint: 'playback' });
    this.nextPlaybackTime = this.audioContext.currentTime;
  }

  setConfig(nextConfig: AudioPlaybackConfig) {
    this.config = { ...nextConfig };
  }

  getConfig(): AudioPlaybackConfig {
    return { ...this.config };
  }

  /**
   * Queue audio for playback
   */
  queueAudio(pcm16Buffer: ArrayBuffer) {
    if (!this.audioContext) {
      return;
    }
    if (this.audioContext.state === 'suspended') {
      void this.audioContext.resume().catch(() => {
        // no-op
      });
    }

    // Convert PCM16 to Float32
    const int16Array = new Int16Array(pcm16Buffer);
    const float32Array = new Float32Array(int16Array.length);
    for (let i = 0; i < int16Array.length; i++) {
      float32Array[i] = int16Array[i] / 32768;
    }

    // Create AudioBuffer
    const audioBuffer = this.audioContext.createBuffer(1, float32Array.length, 24000);
    audioBuffer.copyToChannel(float32Array, 0);

    // Play
    const source = this.audioContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this.audioContext.destination);

    const now = this.audioContext.currentTime;
    const minStart = now + this.config.minLeadTimeSeconds;
    if (this.nextPlaybackTime < minStart) {
      this.nextPlaybackTime = minStart;
    }

    const startAt = this.nextPlaybackTime;
    this.nextPlaybackTime = startAt + audioBuffer.duration;
    this.activeSources.add(source);

    source.onended = () => {
      this.activeSources.delete(source);
      try {
        source.disconnect();
      } catch (e) {
        // ignore
      }
      if (this.audioContext && this.activeSources.size === 0 && this.nextPlaybackTime < this.audioContext.currentTime) {
        this.nextPlaybackTime = this.audioContext.currentTime;
      }
    };

    source.start(startAt);
  }

  hasPendingAudio() {
    if (!this.audioContext) {
      return false;
    }
    const hasScheduledTail = this.nextPlaybackTime > this.audioContext.currentTime + this.config.pendingEpsilonSeconds;
    return this.activeSources.size > 0 || hasScheduledTail;
  }

  /**
   * Stop playback and clear queue
   */
  stop() {
    this.activeSources.forEach((source) => {
      try {
        source.stop();
      } catch (e) {
        // Ignore if already stopped
      }
      try {
        source.disconnect();
      } catch (e) {
        // Ignore disconnect issues
      }
    });
    this.activeSources.clear();
    if (this.audioContext) {
      this.nextPlaybackTime = this.audioContext.currentTime;
    } else {
      this.nextPlaybackTime = 0;
    }
  }

  /**
   * Close audio context
   */
  close() {
    this.stop();
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}

export default useRealtimeVoice;
